import {
  TASK_ANSWER,
  TASK_ANSWER_SUCCESS,
  TASK_FORM_KEEP_SUCCESS,
  TASK_GET_SUCCESS,
  ANSWER_REMOVE_FIELD,
  ANSWER_REMOVE_BLOCK,
  ANSWER_SET_AUTOFOCUS_ROW,
} from '../actions/task';
import { calculateExpiresTask } from '../../api/task';
import { get } from 'object-path';

export const initialState = {
  hasReceivedNullTask: false,
  currentTask: null,
  lastTaskSent: null,
  buffer: [],
  autoFocusRow: null,
};

function removeFieldByPath(state, fieldPath) {
  // fieldPath exemple : informations_0.$items[0].duplicable_1.$items[2].texte_anonyme_0
  const parts = fieldPath.split('.');
  let current = state.currentTask.answer;
  let parent = null;
  let keyOrIndex = null;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (part.startsWith('$items')) {
      // Array of items (rows)
      const idx = parseInt(part.match(/\[(\d+)\]/)[1], 10);
      parent = current.rows;
      keyOrIndex = idx;
      current = parent[idx];
    } else if (current.blocks && Array.isArray(current.blocks)) {
      // find the block by id
      parent = current.blocks;
      keyOrIndex = parent.findIndex(b => b.id === part);
      current = parent[keyOrIndex];
    } else if (current.fields && Array.isArray(current.fields)) {
      // find the field by id
      parent = current.fields;
      keyOrIndex = parent.findIndex(f => f.id === part);
      current = parent[keyOrIndex];
    } else {
      // fallback
      parent = current;
      keyOrIndex = part;
      current = current[part];
    }
  }

  // Remove the field or item
  if (Array.isArray(parent) && keyOrIndex !== -1) {
    parent.splice(keyOrIndex, 1);
  } else if (parent && keyOrIndex) {
    delete parent[keyOrIndex];
  }
}

// We delete answer blocks ONLY if we are not in review mode in order to keep
// all contributions. We remove blocks only if we have a task with hydrated
// blocks and "color" attached to their fields
function removeBlockByPath(state, blockPath) {
  try {
    // Return if no currentTask exists
    if (!state.currentTask || !state.currentTask.answer) {
      return;
    }

    /*
      blockPath examples:
        - informations_0 -> delete all rows in duplicable informations_0
        - informations_0.$items[0] -> delete first item in duplicable informations_0
    */
    const parts = blockPath.split('.');
    let deleteDuplicableItem = false;
    let current = state.currentTask.answer;
    let parent = null;
    let keyOrIndex = null;

    for (let i = 0; i < parts.length; i++) {
      deleteDuplicableItem = false;
      const part = parts[i];
      if (!current) {
        return;
      }

      if (current.blocks && Array.isArray(current.blocks)) {
        // find the block by id
        parent = current.blocks;
        keyOrIndex = parent.findIndex(b => b && b.id === part);
        if (keyOrIndex === -1) {
          return;
        }
        current = parent[keyOrIndex];
      } else if (part.startsWith('$items[')) {
        // find block
        keyOrIndex = part.match(/\$items\[(\d+)\]/)[1];
        const rowsPart = part.replace(/\$items\[(\d+)\]/, 'rows.$1');
        parent = current;
        current = get(current, rowsPart);
        deleteDuplicableItem = true;
      } else {
        // fallback - navigation to the block container
        parent = current;
        keyOrIndex = part;
        current = current[part];
      }
    }

    if (deleteDuplicableItem) {
      parent.rows.splice(keyOrIndex, 1);
    } else {
      // Remove the entire block
      if (Array.isArray(parent) && keyOrIndex !== -1 && keyOrIndex < parent.length) {
        parent.splice(keyOrIndex, 1);
        return;
      } else if (parent && keyOrIndex && parent.hasOwnProperty(keyOrIndex)) {
        delete parent[keyOrIndex];
        return;
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error in removeBlockByPath:', error);
  }
}

export default function taskReducer(state = initialState, action) {
  switch (action.type) {
    case TASK_GET_SUCCESS:
      if (action.payload === null) {
        return {
          ...state,
          hasReceivedNullTask: true,
        };
      }
      return {
        ...state,
        [action.payload.projectId]: action.payload,
        currentTask: state.currentTask ? state.currentTask : action.payload,
        buffer: state.currentTask ? [...state.buffer, action.payload] : state.buffer,
      };
    case TASK_ANSWER: {
      return {
        ...state,
        lastTaskSent: action.fetch.json.IdTask,
      };
    }
    case TASK_ANSWER_SUCCESS: {
      const bufferCopy = state.buffer.slice(0);
      return {
        ...state,
        currentTask: bufferCopy.length ? bufferCopy[0] : null,
        buffer: bufferCopy.slice(1),
      };
    }
    case TASK_FORM_KEEP_SUCCESS: {
      const { taskId } = action.meta;
      const expiresAt = calculateExpiresTask(action?.payload);

      return {
        ...state,
        currentTask:
          state.currentTask.taskId === taskId
            ? { ...state.currentTask, expiresAt }
            : state.currentTask,
        buffer: state.buffer.map((task) =>
          task.taskId === taskId ? { ...task, expiresAt } : task,
        ),
      };
    }
    // Not used
    case ANSWER_REMOVE_FIELD: {
      const { fieldPath } = action.payload;
      const newState = JSON.parse(JSON.stringify(state));
      removeFieldByPath(newState, fieldPath);
      return newState;
    }
    // Used only in "answer" and not in review mode (check function removeBlockByPath for details)
    case ANSWER_REMOVE_BLOCK: {
      const { blockPath } = action.payload;
      const newState = JSON.parse(JSON.stringify(state));
      removeBlockByPath(newState, blockPath);
      return newState;
    }
    case ANSWER_SET_AUTOFOCUS_ROW:
      return { ...state, autoFocusRow: action.payload.rowPath };
    default:
      return state;
  }
}
